import fetch from 'node-fetch';
import { EventSourcePolyfill } from 'event-source-polyfill';

const CONTENT_TYPE = 'application/vnd.bsh.sdk.v1+json';

export class HomeConnectError extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
  }
}

function getApplianceEventSource({ hostname, haId, token }) {
  return new EventSourcePolyfill(`https://${hostname}/api/homeappliances/${haId}/events`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    heartbeatTimeout: 120000,
  });
}

async function getApplianceStatus({ hostname, haId, token, key }) {
  const keyPath = key ? `/${key}` : '';
  const response = await fetch(`https://${hostname}/api/homeappliances/${haId}/status${keyPath}`, {
    headers: {
      Accept: CONTENT_TYPE,
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  });

  const wrapper = await response.json();

  if (wrapper.error) {
    throw new HomeConnectError('Failed to get appliance status', wrapper.error);
  }
  return wrapper.data.status || wrapper.data;
}

async function getApplianceSettings({ hostname, haId, token }) {
  const response = await fetch(`https://${hostname}/api/homeappliances/${haId}/settings`, {
    headers: {
      Accept: CONTENT_TYPE,
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  });

  const wrapper = await response.json();

  if (wrapper.error) {
    throw new HomeConnectError('Failed to get appliance settings', wrapper.error);
  }
  return wrapper.data.settings;
}

async function getApplianceProgram({ hostname, haId, token, program }) {
  const response = await fetch(
    `https://${hostname}/api/homeappliances/${haId}/programs/available/${program}`,
    {
      headers: {
        Accept: CONTENT_TYPE,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    },
  );

  const wrapper = await response.json();
  if (wrapper.error) {
    throw new HomeConnectError(`Failed to get appliance program for ${program}`, wrapper.error);
  }
  return wrapper.data;
}

async function getAppliancePrograms({ hostname, haId, token }) {
  const response = await fetch(`https://${hostname}/api/homeappliances/${haId}/programs`, {
    headers: {
      Accept: CONTENT_TYPE,
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  });

  const wrapper = await response.json();
  if (wrapper.error) {
    throw new HomeConnectError('Failed to get appliance programs', wrapper.error);
  }
  return wrapper.data.programs;
}

async function stopActiveProgram({ hostname, haId, token }) {
  const response = await fetch(`https://${hostname}/api/homeappliances/${haId}/programs/active`, {
    headers: {
      Accept: CONTENT_TYPE,
      Authorization: `Bearer ${token}`,
    },
    method: 'DELETE',
  });

  if (response.status !== 204) {
    const wrapper = await response.json();
    throw new HomeConnectError('Failed to stop appliance programs', wrapper.error);
  }
}

async function setApplianceProgram({ hostname, haId, token, program, options, selectedOrActive }) {
  const response = await fetch(
    `https://${hostname}/api/homeappliances/${haId}/programs/${selectedOrActive}`,
    {
      headers: {
        Accept: CONTENT_TYPE,
        Authorization: `Bearer ${token}`,
        'Content-Type': CONTENT_TYPE,
      },
      body: JSON.stringify({
        data: {
          key: program,
          options,
        },
      }),
      method: 'PUT',
    },
  );

  if (response.status !== 204) {
    const wrapper = await response.json();
    throw new HomeConnectError(`Failed to set ${selectedOrActive} program`, wrapper.error);
  }
}

async function activateProgram(params) {
  return setApplianceProgram({ ...params, selectedOrActive: 'active' });
}

async function selectProgram(params) {
  return setApplianceProgram({ ...params, selectedOrActive: 'selected' });
}

async function getProgramOptions({ hostname, haId, token, selectedOrActive }) {
  const response = await fetch(
    `https://${hostname}/api/homeappliances/${haId}/programs/${selectedOrActive}/options`,
    {
      headers: {
        Accept: CONTENT_TYPE,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    },
  );
  const wrapper = await response.json();
  if (wrapper.error) {
    throw new HomeConnectError('Failed to get appliance program options', wrapper.error);
  }
  return wrapper.data.options;
}

async function setProgramOptions({ hostname, haId, token, options, selectedOrActive }) {
  const response = await fetch(
    `https://${hostname}/api/homeappliances/${haId}/programs/${selectedOrActive}/options`,
    {
      headers: {
        Accept: CONTENT_TYPE,
        Authorization: `Bearer ${token}`,
        'Content-Type': CONTENT_TYPE,
      },
      body: JSON.stringify({
        data: {
          options,
        },
      }),
      method: 'PUT',
    },
  );
  if (response.status !== 204) {
    const wrapper = await response.json();
    throw new HomeConnectError(`Failed to set ${selectedOrActive} program`, wrapper.error);
  }
}

async function setActiveProgramOptions(params) {
  return setProgramOptions({ ...params, selectedOrActive: 'active' });
}

async function setSelectedProgramOptions(params) {
  return setProgramOptions({ ...params, selectedOrActive: 'selected' });
}

async function getActiveProgramOptions(params) {
  return getProgramOptions({ ...params, selectedOrActive: 'active' });
}

async function getSelectedProgramOptions(params) {
  return getProgramOptions({ ...params, selectedOrActive: 'selected' });
}

async function getProgram({ hostname, haId, token, selectedOrActive }) {
  const response = await fetch(
    `https://${hostname}/api/homeappliances/${haId}/programs/${selectedOrActive}`,
    {
      headers: {
        Accept: CONTENT_TYPE,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    },
  );
  if (response.status === 404) {
    return null;
  }
  const wrapper = await response.json();
  if (wrapper.error) {
    throw new HomeConnectError(`Failed to get ${selectedOrActive} program`, wrapper.error);
  }
  return wrapper.data;
}

async function getActiveProgram(params) {
  return getProgram({ ...params, selectedOrActive: 'active' });
}

async function getSelectedProgram(params) {
  return getProgram({ ...params, selectedOrActive: 'selected' });
}

async function getAppliances({ hostname, token }) {
  const response = await fetch(`https://${hostname}/api/homeappliances`, {
    headers: {
      Accept: CONTENT_TYPE,
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  });

  const wrapper = await response.json();
  if (wrapper.error) {
    throw new HomeConnectError('Failed to get appliances', wrapper.error);
  }
  return wrapper.data.homeappliances;
}

export function getAuthUrl({ hostname, clientId, baseUrl, pathname }) {
  const redirectUri = encodeURIComponent(`${baseUrl}/login/homeconnect/return`);
  const scope = 'IdentifyAppliance%20Oven';
  return `https://${hostname}/security/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&realm=apiclient.home-connect.com&state=${encodeURIComponent(
    pathname.startsWith('/w/') ? pathname : `/w${pathname}`,
  )}`;
}

export default {
  getAppliances,
  getApplianceEventSource,
  getApplianceSettings,
  getApplianceStatus,
  getApplianceProgram,
  getAppliancePrograms,
  getActiveProgramOptions,
  setActiveProgramOptions,
  getSelectedProgramOptions,
  setSelectedProgramOptions,
  activateProgram,
  getActiveProgram,
  selectProgram,
  getSelectedProgram,
  stopActiveProgram,
  getAuthUrl,
};
